import { ProcessingMintRequest } from '@flipster/apis/v1/dynamic-earn/types';
import { FormattedMessage, type MessageKey } from '@flipster/i18n';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Modal } from '@flipster/react/design-system/modal';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import InProgressIcon from '@flipster/react/icon/generated/InProgressIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import { numberFormatter } from '@flipster/utils/num';

import { getEstimatedHours } from './date';

const subscribeTitleId: MessageKey = 'Earn.r251212.Subscribe_amount_product';
const redeemTitleId: MessageKey = 'Earn.r251212.Redeem_amount_product';
const productNameMap: Record<string, string> = {
  NRSUSDTP: 'Narsil USDT Prime',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  pendingRequests: ProcessingMintRequest[];
}

export const DynamicEarnProcessingModal = ({ isOpen, onClose, onExit, pendingRequests }: Props) => {
  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title
          label={
            <FormattedMessage id='Earn.r251204.Processing_count_status' values={{ count: pendingRequests.length }} />
          }
        />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 16 })}>
          {pendingRequests.map((request) => (
            <PendingRequestItem key={request.confirmedAt} request={request} />
          ))}
          <ul
            className={stack({
              gap: 4,
              pt: 16,
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
              borderColor: 'border.default.level-1',
              list: 'disc',
              textStyle: 'Text/S/Regular',
              color: 'content.default.level-2',
            })}
          >
            <li>
              <FormattedMessage id='Earn.r251212.Processing_time_notice' />
            </li>
            <li>
              <FormattedMessage id='Earn.r251204.Redemption_amount_variance_notice_with_fees' />
            </li>
            <li>
              <FormattedMessage id='Earn.r260108.Processing_notification_notice' />
            </li>
          </ul>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};

const PendingRequestItem = ({ request }: { request: ProcessingMintRequest }) => {
  return (
    <li className={stack({ py: 12, gap: 12 })}>
      <div className={hstack({ gap: 16 })}>
        <InProgressIcon size={24} />
        <div className={stack({ gap: 2 })}>
          <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
            {request.side === 'MINT' && (
              <FormattedMessage
                id={subscribeTitleId}
                values={{
                  amountSymbol: `${numberFormatter(request.fromAmount, { precision: UsdtPrecision, withComma: true, toFixed: true })} ${request.collateralCurrency}`,
                  productName: productNameMap[request.providerCurrency] ?? request.providerCurrency,
                }}
              />
            )}
            {request.side === 'REDEEM' && (
              <FormattedMessage
                id={redeemTitleId}
                values={{
                  amountSymbol: `${numberFormatter(request.estimatedToAmount, { precision: UsdtPrecision, withComma: true, toFixed: true })} ${request.collateralCurrency}`,
                  productName: productNameMap[request.providerCurrency] ?? request.providerCurrency,
                }}
              />
            )}
          </p>
          <p className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-2' })}>
            <Datetime value={request.confirmedAt} format='yyyy.MM.dd HH:mm:ss' isUTC withUTCText />
          </p>
        </div>
      </div>
      <div className={css({ pl: 40, textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-3' })}>
        {request.side === 'MINT' && (
          <p>
            <FormattedMessage id='Earn.r251204.Est_processing_time_colon' />{' '}
            <EstimatedProcecssingTime estimatedHours={getEstimatedHours(request.estimateCompleteAt)} />
          </p>
        )}
        {request.side === 'REDEEM' && (
          <>
            <p>
              <FormattedMessage id='Earn.r251204.Period_type_colon' />{' '}
              {request.type === 'STANDARD' && <FormattedMessage id='Earn.r260108.Standard' />}
              {request.type === 'INSTANT' && <FormattedMessage id='Earn.r260108.Instant' />}
            </p>
            <p>
              <FormattedMessage id='Earn.r251204.Est_processing_time_colon' />{' '}
              <EstimatedProcecssingTime estimatedHours={getEstimatedHours(request.estimateCompleteAt)} />
            </p>
            <p>
              <FormattedMessage id='Earn.r251204.You_receive_colon' />{' '}
              <FormattedMessage id='Earn.r251204.Determined_at_execution_value' />
            </p>
            <p>
              <FormattedMessage id='Earn.r260304.Shares_colon' />{' '}
              <Formatted.Number value={request.shareTokenAmount} precision={6} toFixed />
            </p>
          </>
        )}
      </div>
    </li>
  );
};

const EstimatedProcecssingTime = ({ estimatedHours }: { estimatedHours: number }) => {
  return estimatedHours > 0 ? (
    <FormattedMessage id='Earn.r251204.Within_hours_duration' values={{ hour: estimatedHours }} />
  ) : (
    <div className={css({ display: 'inline-flex', alignItems: 'center' })}>
      <FormattedMessage id='Earn.r260112.Still_processing' />
      <IconButton
        icon={<InformationCircleIcon />}
        hierarchy='quaternary'
        size='small'
        onClick={() =>
          dialog.open({
            title: {
              leftAddon: <InProgressIcon />,
              label: <FormattedMessage id='Earn.r260112.Your_request_is_still_processing' />,
            },
            content: <FormattedMessage id='Earn.r260112.Your_request_is_still_processing_description' />,
            buttons: {
              primary: <Dialog.Button hierarchy='primary' label={<FormattedMessage id='Common.r250918.Got_it' />} />,
            },
          })
        }
      />
    </div>
  );
};
